import {
  activeAgent,
  applyDiscovery,
  decodeAgentHelloV2,
  initialLink,
  isLocalAgentError,
  linkPhase,
  type AgentRef,
  type LinkSnapshot,
  type LocalAgentClient,
} from '@krp/client-runtime'
import {
  ATOL_DEFAULT_MODEL,
  ATOL_DEFAULT_PORT,
  atolSettingsFrom,
  autoScanDue,
  decodeAtolEquipment,
  decodeAtolTest,
  decodeDeviceState,
  decodeDriversList,
  decodeEquipmentList,
  decodeEquipmentScan,
  decodeTerminalPing,
  decodeTerminalSaved,
  decodeTerminals,
  driverBecameReady,
  FULL_DISCOVERY_EVERY_MS,
  pollDelay,
  resolveDrivers,
  scanRunning,
  supportInfo,
  type AtolEquipment,
  type ConnectRequest,
  type DeviceState,
  type DriverCategory,
  type DriverStatus,
  type EquipmentCandidate,
  type EquipmentSnapshot,
  type HardwareActions,
  type HardwareLinks,
  type HardwareNotice,
  type HardwareTabId,
  type LinkCheck,
  type ScanRun,
  type TerminalsData,
} from '@krp/platform-ui'

/**
 * Состояние окна «Оборудование» (EQW2-W2): ведёт `EquipmentSnapshot`. Окно само находит программу-драйвер на этом
 * компьютере (127.0.0.1, диапазон портов), перепроверяет, пока открыто, сканирует по драйверам и выполняет действия
 * человека. Это состояние ЭТОГО компьютера, а не данные сервера: в кэш запросов и в хранилище оно не попадает.
 *
 * Правила безопасности (п. 9 проекта): автоматически — только чтение и сканирование; «Подключить» не создаёт чек, смену
 * или оплату; после возврата связи ничего не повторяется за человека; проверка связи терминала — по кнопке и один раз
 * после «Подключить». Контроллер не знает React: его ведёт хук `useHardware`, а тесты гоняют обоих.
 */

/** Предел ожидания ответа сканирования одного драйвера, мс: агент отвечает за 25 с, окно ждёт 30. */
export const SCAN_TIMEOUT_MS = 30_000

/** Кандидат-подсказка «касса по COM?»: принадлежит драйверу АТОЛ. */
const COM_HINT_KIND = 'atol-com-hint'
const ATOL_ID = 'atol-kkt'

const CATEGORIES: readonly DriverCategory[] = ['fiscal-register', 'payment-terminal']

export interface HardwareModel {
  link: LinkSnapshot
  now: number
  drivers: readonly DriverStatus[] | null
  equipment: AtolEquipment | null
  device: DeviceState | null
  terminals: TerminalsData | null
  scans: Readonly<Record<string, ScanRun>>
  autoScanned: readonly DriverCategory[]
  kassaTest: LinkCheck | null
  terminalChecks: Readonly<Record<string, LinkCheck>>
  busy: boolean
  checking: boolean
  notice: HardwareNotice | null
  /** Первый просмотр закончен: можно выбирать вкладку по умолчанию (п. 2.3). */
  observed: boolean
}

export function toSnapshot(model: HardwareModel, now: number = model.now): EquipmentSnapshot {
  return {
    link: model.link,
    now,
    drivers: model.drivers,
    equipment: model.equipment,
    device: model.device,
    terminals: model.terminals,
    scans: model.scans,
    autoScanned: model.autoScanned,
    kassaTest: model.kassaTest,
    terminalChecks: model.terminalChecks,
  }
}

export interface ControllerDeps {
  client: LocalAgentClient
  links: HardwareLinks
  siteOrigin: string
  minApiLevel: number
  onChange: (model: HardwareModel) => void
}

/** То, что можно подменить без пересоздания контроллера: часы, открытие ссылки, копирование. */
export interface ControllerEnvironment {
  clock: () => number
  openUrl: (url: string) => void
  copyText: (text: string) => Promise<void>
}

export interface HardwareController {
  /** Подставить свежие часы и обработчики (хук делает это на каждой перерисовке). */
  configure: (environment: Partial<ControllerEnvironment>) => void
  getModel: () => HardwareModel
  start: () => void
  stop: () => void
  setTab: (tab: HardwareTabId | null) => void
  actions: HardwareActions
}

const isRefused = (reason: unknown, ...statuses: number[]): boolean =>
  isLocalAgentError(reason) && reason.kind === 'refused' && reason.status !== undefined && statuses.includes(reason.status)

/** 401/403: агент не пускает этот сайт к настройке оборудования. */
const isDenied = (reason: unknown): boolean => isLocalAgentError(reason) && (reason.kind === 'unauthorized' || reason.status === 403)

const failureText = (reason: unknown, fallback: string): string => (isLocalAgentError(reason) ? reason.message : fallback)

/** Какому драйверу принадлежит найденное: свой вид, а подсказка про COM — драйверу АТОЛ. */
const ownerOf = (candidate: EquipmentCandidate): string => (candidate.kind === COM_HINT_KIND ? ATOL_ID : candidate.kind)

const defaultEnvironment: ControllerEnvironment = {
  clock: () => Date.now(),
  openUrl: (url) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  },
  copyText: (text) => navigator.clipboard.writeText(text),
}

export function createHardwareController(deps: ControllerDeps, initial: Partial<ControllerEnvironment> = {}): HardwareController {
  const { client } = deps
  let environment: ControllerEnvironment = { ...defaultEnvironment, ...initial }
  const clock = () => environment.clock()

  let model: HardwareModel = {
    link: initialLink(deps.siteOrigin, deps.minApiLevel),
    now: clock(),
    drivers: null,
    equipment: null,
    device: null,
    terminals: null,
    scans: {},
    autoScanned: [],
    kassaTest: null,
    terminalChecks: {},
    busy: false,
    checking: false,
    notice: null,
    observed: false,
  }
  let alive = false
  let tab: HardwareTabId | null = null
  let tickNumber = 0
  let running = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let lastFullDiscovery = Number.NEGATIVE_INFINITY
  let noAgentSince: number | null = null
  let loaded = false
  const pendingInstallScan = new Set<DriverCategory>()
  const runTokens = new Map<string, number>()
  let tokenSeq = 0

  function commit(patch: Partial<HardwareModel>): void {
    model = { ...model, ...patch, now: clock() }
    if (alive) deps.onChange(model)
  }

  const snapshot = (): EquipmentSnapshot => toSnapshot(model, clock())

  // ---- опрос ---------------------------------------------------------------------------------------------------

  function schedule(): void {
    if (!alive) return
    if (timer !== null) clearTimeout(timer)
    const delay = pollDelay(snapshot(), noAgentSince)
    timer = setTimeout(() => {
      timer = null
      // Пауза, пока вкладка браузера скрыта; при возвращении сработает visibilitychange.
      if (typeof document !== 'undefined' && document.hidden) {
        schedule()
        return
      }
      void tick(false)
    }, delay)
  }

  function onVisibility(): void {
    if (typeof document !== 'undefined' && !document.hidden) void tick(false)
  }

  /** Один тик. `force` — по просьбе человека или после его действия: новый тик делает прежний устаревшим. */
  async function tick(force: boolean): Promise<void> {
    if (!alive) return
    if (running && !force) return
    const mine = ++tickNumber
    running = true
    if (force) commit({ checking: true })
    try {
      await observe(mine, force)
    } catch {
      // Сбой одного тика (например, сломанный ответ) окно не роняет: следующий тик попробует снова.
    } finally {
      if (mine === tickNumber) {
        running = false
        if (alive && model.checking) commit({ checking: false })
      }
      schedule()
    }
  }

  const stale = (mine: number): boolean => !alive || mine !== tickNumber

  async function observe(mine: number, force: boolean): Promise<void> {
    const started = clock()
    const link0 = model.link
    const selected = link0.selected
    let found: AgentRef[]
    let full = true
    if (!force && selected !== null && link0.agents.length > 0 && started - lastFullDiscovery < FULL_DISCOVERY_EVERY_MS) {
      // Агент на связи: спрашиваем только его hello (промах считает только он).
      full = false
      const one = await client.probe(selected.port, decodeAgentHelloV2)
      found = one === null ? [] : link0.agents.map((agent) => (agent.port === one.port ? { port: one.port, hello: one.hello } : agent))
    } else {
      // Агента нет, потерян, пора искать второго или человек попросил проверить: полный просмотр всего диапазона.
      const list = await client.discoverAll(decodeAgentHelloV2)
      found = list.map((item) => ({ port: item.port, hello: item.hello }))
    }
    if (stale(mine)) return
    if (full) lastFullDiscovery = clock()

    const now = clock()
    let link = applyDiscovery(model.link, found, now)
    if (found.length === 0) noAgentSince = noAgentSince ?? now
    else noAgentSince = null

    const phase = linkPhase(link, now)
    const agent = activeAgent(link)
    if (found.length === 0 || agent === null || (phase !== 'connected' && phase !== 'outdated')) {
      commit({ link, observed: true })
      return
    }

    const read = await readAgent(agent, link)
    if (stale(mine)) return
    link = read.link

    // Драйвер только что стал готовым (поставили — окно заметило): один автоскан после установки (п. 4.1).
    let autoScanned = model.autoScanned
    let scans = model.scans
    if (loaded) {
      const before = resolveDrivers(snapshot())
      const after = resolveDrivers({ ...snapshot(), drivers: read.drivers, terminals: read.terminals })
      for (const category of CATEGORIES) {
        if (!driverBecameReady(before, after, category)) continue
        autoScanned = autoScanned.filter((item) => item !== category)
        scans = Object.fromEntries(Object.entries(scans).filter(([, run]) => run.category !== category))
        pendingInstallScan.add(category)
      }
    }
    loaded = true
    commit({
      link,
      drivers: read.drivers,
      equipment: read.equipment,
      device: read.device,
      terminals: read.terminals,
      autoScanned,
      scans,
      observed: true,
    })
    runAutoScans()
  }

  interface Read {
    link: LinkSnapshot
    drivers: readonly DriverStatus[] | null
    equipment: AtolEquipment | null
    device: DeviceState | null
    terminals: TerminalsData | null
    }

  async function readAgent(agent: AgentRef, link: LinkSnapshot): Promise<Read> {
    const port = agent.port
    const [driversResult, equipmentResult, terminalsResult] = await Promise.allSettled([
      client.request({ port, path: '/v1/drivers', decode: decodeDriversList }),
      client.request({ port, path: '/v1/equipment', decode: decodeEquipmentList }),
      client.request({ port, path: '/v1/terminals', decode: decodeTerminals }),
    ])

    let drivers = model.drivers
    if (driversResult.status === 'fulfilled') drivers = driversResult.value
    else if (isRefused(driversResult.reason, 404, 405)) drivers = null

    let equipment = model.equipment
    let denied = false
    if (equipmentResult.status === 'fulfilled') equipment = equipmentResult.value
    else if (isDenied(equipmentResult.reason)) denied = true

    let terminals = model.terminals
    if (!denied) {
      if (terminalsResult.status === 'fulfilled') {
        terminals = terminalsResult.value
      }
    }

    let device = model.device
    if (!denied) {
      if (equipment?.active === true) {
        try {
          device = await client.request({ port, path: '/v1/device', decode: decodeDeviceState })
        } catch {
          device = null
        }
      } else {
        device = null
      }
    }
    return { link: { ...link, equipmentDenied: denied }, drivers, equipment, device, terminals }
  }

  // ---- сканирование --------------------------------------------------------------------------------------------

  /** Автоскан (п. 4.1): один раз на категорию — при первом показе вкладки или после установки драйвера. */
  function runAutoScans(): void {
    if (!alive) return
    for (const category of CATEGORIES) {
      const onTab = (category === 'fiscal-register' && tab === 'kassas') || (category === 'payment-terminal' && tab === 'terminals')
      const afterInstall = pendingInstallScan.has(category)
      if (!onTab && !afterInstall) continue
      pendingInstallScan.delete(category)
      if (!autoScanDue(snapshot(), category)) continue
      commit({ autoScanned: [...model.autoScanned, category] })
      void runScan(category)
    }
  }

  /** Сканирование категории по драйверам отдельными запросами; идущее сканирование категории повторно не запускается. */
  async function runScan(category: DriverCategory, comPorts?: readonly string[]): Promise<void> {
    if (!alive) return
    const snap = snapshot()
    if (scanRunning(snap, category)) return
    const agent = activeAgent(model.link)
    if (agent === null) return
    const ready = resolveDrivers(snap).filter((driver) => driver.state === 'ready')
    const targets = ready.filter((driver) => driver.category === category)
    if (targets.length === 0) return
    const perDriver = (agent.hello.api?.features ?? []).includes('scan-kinds')

    const startedAt = clock()
    const tokens = new Map<string, number>()
    const started: Record<string, ScanRun> = {}
    for (const driver of targets) {
      const token = ++tokenSeq
      tokens.set(driver.id, token)
      runTokens.set(driver.id, token)
      started[driver.id] = {
        driverId: driver.id,
        category,
        state: 'running',
        startedAt,
        finishedAt: null,
        candidates: [],
        hints: { comPorts: [] },
        error: null,
      }
    }
    commit({ scans: { ...model.scans, ...started } })

    const call = (kinds?: readonly string[]) =>
      client.request({
        port: agent.port,
        method: 'POST',
        path: '/v1/equipment/scan',
        body: { ...(kinds === undefined ? {} : { kinds }), ...(comPorts !== undefined && comPorts.length > 0 ? { comPorts } : {}) },
        timeoutMs: SCAN_TIMEOUT_MS,
        decode: decodeEquipmentScan,
      })

    /** Записать итог, только если этот запуск ещё ждут: отменённый или вытесненный ответ окно не трогает. */
    const finish = (driverId: string, patch: Partial<ScanRun>): void => {
      const current = model.scans[driverId]
      if (current === undefined || current.state !== 'running' || runTokens.get(driverId) !== tokens.get(driverId)) return
      commit({ scans: { ...model.scans, [driverId]: { ...current, ...patch, finishedAt: clock() } } })
    }
    const fail = (driverId: string, reason: unknown): void => {
      // Нет ответа за предел — строка драйвера скажет «не ответил за 25 секунд»; отказ агента — его слова дословно.
      finish(driverId, { state: 'failed', error: isLocalAgentError(reason) && reason.kind !== 'unreachable' ? reason.message : null })
    }

    if (perDriver) {
      await Promise.all(
        targets.map(async (driver) => {
          try {
            const result = await call([driver.id])
            if (result.candidates.length === 0 && result.errors.length > 0) {
              finish(driver.id, { state: 'failed', error: result.errors.join(' ') })
            } else {
              finish(driver.id, { state: 'done', candidates: result.candidates, hints: result.hints })
            }
          } catch (reason) {
            fail(driver.id, reason)
          }
        }),
      )
      return
    }

    // Агент без `kinds`: сканирует всё сразу. Итог раскладываем по драйверам, чтобы экран видел то же, что при раздельных запросах.
    try {
      const result = await call()
      const failed = result.candidates.filter((candidate) => targets.some((driver) => driver.id === ownerOf(candidate))).length === 0 && result.errors.length > 0
      for (const driver of ready) {
        const own = result.candidates.filter((candidate) => ownerOf(candidate) === driver.id)
        const hints = driver.id === ATOL_ID ? result.hints : { comPorts: [] }
        if (targets.some((target) => target.id === driver.id)) {
          if (failed && own.length === 0) finish(driver.id, { state: 'failed', error: result.errors.join(' ') })
          else finish(driver.id, { state: 'done', candidates: own, hints })
        } else if (model.scans[driver.id]?.state !== 'running') {
          const at = clock()
          commit({
            scans: {
              ...model.scans,
              [driver.id]: {
                driverId: driver.id,
                category: driver.category,
                state: 'done',
                startedAt,
                finishedAt: at,
                candidates: own,
                hints,
                error: null,
              },
            },
          })
        }
      }
    } catch (reason) {
      for (const driver of targets) fail(driver.id, reason)
    }
  }

  // ---- действия человека ---------------------------------------------------------------------------------------

  /** Действие с агентом: занято на время запроса, отказ — словами агента, потом перечитываем состояние. */
  async function guarded(work: () => Promise<HardwareNotice | null>, failure: string): Promise<void> {
    if (!alive || model.busy) return
    commit({ busy: true, notice: null })
    try {
      const done = await work()
      if (done !== null) commit({ notice: done })
    } catch (reason) {
      commit({ notice: { tone: 'error', text: failureText(reason, failure) } })
    } finally {
      commit({ busy: false })
      await tick(true)
    }
  }

  function requirePort(): number {
    const agent = activeAgent(model.link)
    if (agent === null) throw new Error('Нет связи с программой-драйвером.')
    return agent.port
  }

  async function atolTest(port: number): Promise<LinkCheck> {
    try {
      const result = await client.request({ port, method: 'POST', path: '/v1/equipment/atol/test', decode: decodeAtolTest })
      return { ok: result.ok, message: result.message, checkedAt: clock() }
    } catch (reason) {
      return { ok: false, message: failureText(reason, 'Не удалось проверить связь с кассой.'), checkedAt: clock() }
    }
  }

  async function terminalTest(port: number, id: string): Promise<LinkCheck> {
    try {
      const ping = await client.request({ port, method: 'POST', path: `/v1/terminals/${encodeURIComponent(id)}/test`, decode: decodeTerminalPing })
      return { ok: ping.ok, message: ping.message, checkedAt: clock() }
    } catch (reason) {
      return { ok: false, message: failureText(reason, 'Не удалось проверить связь с терминалом.'), checkedAt: clock() }
    }
  }

  async function connect(request: ConnectRequest): Promise<void> {
    await guarded(async () => {
      const port = requirePort()
      if (request.category === 'fiscal-register') {
        const settings = atolSettingsFrom(request.values)
        await client.request({
          port,
          method: 'PUT',
          path: '/v1/equipment/atol',
          body: { enabled: true, model: settings.model, port: settings.port, comFile: settings.comFile },
          decode: decodeAtolEquipment,
        })
        // Сразу один раз проверяем связь; сбой проверки не отменяет включения — человек видит слова агента.
        const check = await atolTest(port)
        commit({ kassaTest: check })
        return check.ok
          ? { tone: 'info', text: 'Касса включена.' }
          : { tone: 'error', text: `Касса включена, но связь не подтверждена: ${check.message}` }
      }
      const saved = await client.request({
        port,
        method: 'PUT',
        path: `/v1/terminals/${encodeURIComponent(request.candidateId)}`,
        body: { kind: request.kind, values: request.values },
        decode: decodeTerminalSaved,
      })
      // Найденное, которое уже сохранили, из списка уходит.
      commit({
        scans: Object.fromEntries(
          Object.entries(model.scans).map(([id, run]) => [id, { ...run, candidates: run.candidates.filter((item) => item.candidateId !== request.candidateId) }]),
        ),
      })
      const check = await terminalTest(port, saved.id)
      commit({ terminalChecks: { ...model.terminalChecks, [saved.id]: check } })
      return check.ok
        ? { tone: 'info', text: 'Терминал сохранён, связь есть.' }
        : { tone: 'error', text: `Терминал сохранён, но связь не подтверждена: ${check.message}` }
    }, request.category === 'fiscal-register' ? 'Не удалось включить кассу.' : 'Не удалось сохранить терминал.')
  }

  const actions: HardwareActions = {
    recheck: () => {
      commit({ notice: null })
      void tick(true)
    },
    downloadAgent: () => {
      if (deps.links.agentInstaller !== null) environment.openUrl(deps.links.agentInstaller)
    },
    pickAgent: (port) => {
      const chosen = model.link.agents.find((agent) => agent.port === port)
      if (chosen === undefined) return
      commit({ link: { ...model.link, selected: chosen } })
      void tick(true)
    },
    copyOrigin: () => {
      void environment.copyText(deps.siteOrigin).then(
        () => commit({ notice: { tone: 'info', text: 'Адрес сайта скопирован.' } }),
        () => commit({ notice: { tone: 'error', text: 'Не удалось скопировать: выделите адрес и скопируйте вручную.' } }),
      )
    },
    downloadDriver: (driverId) => {
      const url = deps.links.drivers[driverId] ?? null
      if (url !== null) environment.openUrl(url)
    },
    checkDrivers: () => {
      void tick(true)
    },
    copyDiagnostics: () => {
      void environment.copyText(supportInfo(snapshot())).then(
        () => commit({ notice: { tone: 'info', text: 'Сведения скопированы.' } }),
        () => commit({ notice: { tone: 'error', text: 'Не удалось скопировать сведения.' } }),
      )
    },
    scan: (category) => {
      void runScan(category)
    },
    cancelScan: (category) => {
      const at = clock()
      commit({
        scans: Object.fromEntries(
          Object.entries(model.scans).map(([id, run]) => [id, run.category === category && run.state === 'running' ? { ...run, state: 'cancelled' as const, finishedAt: at } : run]),
        ),
      })
    },
    scanComPort: (port) => {
      void runScan('fiscal-register', [port])
    },
    connect: (request) => {
      void connect(request)
    },
    disableKassa: () => {
      void guarded(async () => {
        const port = requirePort()
        const current = model.equipment?.settings
        await client.request({
          port,
          method: 'PUT',
          path: '/v1/equipment/atol',
          body: { enabled: false, model: current?.model ?? ATOL_DEFAULT_MODEL, port: current?.port ?? ATOL_DEFAULT_PORT, comFile: current?.comFile ?? null },
          decode: decodeAtolEquipment,
        })
        commit({ kassaTest: null })
        return { tone: 'info', text: 'Касса выключена.' }
      }, 'Не удалось выключить кассу.')
    },
    testKassa: () => {
      void guarded(async () => {
        commit({ kassaTest: await atolTest(requirePort()) })
        return null
      }, 'Не удалось проверить связь с кассой.')
    },
    testTerminal: (id) => {
      void guarded(async () => {
        const check = await terminalTest(requirePort(), id)
        commit({ terminalChecks: { ...model.terminalChecks, [id]: check } })
        return null
      }, 'Не удалось проверить связь с терминалом.')
    },
    activateTerminal: (id) => {
      void guarded(async () => {
        await client.request({ port: requirePort(), method: 'POST', path: `/v1/terminals/${encodeURIComponent(id)}/activate`, decode: decodeTerminalSaved })
        return { tone: 'info', text: 'Терминал назначен основным.' }
      }, 'Не удалось сделать терминал основным.')
    },
    removeTerminal: (id) => {
      void guarded(async () => {
        await client.request({ port: requirePort(), method: 'DELETE', path: `/v1/terminals/${encodeURIComponent(id)}`, decode: () => null })
        commit({ terminalChecks: Object.fromEntries(Object.entries(model.terminalChecks).filter(([key]) => key !== id)) })
        return { tone: 'info', text: 'Терминал удалён.' }
      }, 'Не удалось удалить терминал.')
    },
  }

  return {
    configure: (next) => {
      environment = { ...environment, ...next }
    },
    getModel: () => model,
    start: () => {
      if (alive) return
      alive = true
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)
      deps.onChange(model)
      void tick(false)
    },
    stop: () => {
      alive = false
      running = false
      if (timer !== null) clearTimeout(timer)
      timer = null
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility)
    },
    setTab: (next) => {
      tab = next
      runAutoScans()
    },
    actions,
  }
}
